import React, { useState, useRef, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    TextField,
    Button,
    Paper,
    Autocomplete,
    Grid,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    InputAdornment
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { useReactToPrint } from 'react-to-print';
import { getFornecedores, getInvestidores, getObras } from '../services/api';
import { Fornecedor, Investidor, Obra } from '../services/types';
import ReciboPrint, { Pagador } from '../components/ReciboPrint';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import IconButton from '@mui/material/IconButton';
import { ReciboItem } from '../components/ReciboPrint';

interface ItemMaterialForm {
    id: string;
    descricao: string;
    quantidade: string;
    valor: string;
}

const RecibosMaterialPage: React.FC = () => {
    const [fornecedores, setFornecedores] = useState<Fornecedor[]>([]);
    const [investidores, setInvestidores] = useState<Investidor[]>([]);
    const [obras, setObras] = useState<Obra[]>([]);

    // Form State
    const [selectedFornecedor, setSelectedFornecedor] = useState<Fornecedor | null>(null);
    const [selectedInvestidor, setSelectedInvestidor] = useState<Investidor | null>(null);
    const [selectedObra, setSelectedObra] = useState<Obra | null>(null);
    const [itens, setItens] = useState<ItemMaterialForm[]>([{ id: Date.now().toString(), descricao: '', quantidade: '1', valor: '' }]);
    const [data, setData] = useState<string>(dayjs().format('YYYY-MM-DD'));
    const [cidade, setCidade] = useState<string>('Rio das Ostras');
    const [estado, setEstado] = useState<string>('RJ');
    const [tipoDocumento, setTipoDocumento] = useState<'CPF' | 'CNPJ'>('CNPJ');

    // Pagador Interveniente State
    const [pagadorInterveniente, setPagadorInterveniente] = useState<boolean>(false);
    const [selectedPagador, setSelectedPagador] = useState<Pagador | null>(null);

    const [quitaIntegralmente, setQuitaIntegralmente] = useState<boolean>(true);

    // Computed Pagadores Possiveis
    // Helper to filter material providers (or those with undefined type, defaulting to material too)
    const isFornecedorMaterial = (f: Fornecedor) => !f.tipo || f.tipo === 'MATERIAL' || f.tipo === 'AMBOS';

    const fornecedoresMaterial = fornecedores.filter(isFornecedorMaterial);

    const pagadoresPossiveis: Pagador[] = [
        ...investidores
            .filter(i => !selectedInvestidor || i.id !== selectedInvestidor.id) // Exclude selected Contratante
            .map(i => ({ id: i.id, nome: i.nome, documento: i.cpf_cnpj, tipo: 'Investidor' as const })),
        ...fornecedoresMaterial.map(f => ({ id: f.id, nome: f.nome_razao_social, documento: f.cnpj_cpf, tipo: 'Fornecedor' as const }))
    ];

    const totalValor = itens.reduce((sum, item) => sum + (parseFloat(item.valor) || 0), 0);

    const componentRef = useRef<HTMLDivElement>(null);

    const handlePrint = useReactToPrint({
        contentRef: componentRef,
        documentTitle: `Recibo_${selectedFornecedor?.nome_razao_social || 'Material'}_${data}`,
    });

    useEffect(() => {
        const loadData = async () => {
            try {
                const [fornecedoresData, investidoresData, obrasData] = await Promise.all([
                    getFornecedores(),
                    getInvestidores(),
                    getObras()
                ]);

                setFornecedores(fornecedoresData || []);
                setInvestidores(investidoresData || []);
                setObras(obrasData || []);
            } catch (error) {
                console.error("Erro ao carregar dados", error);
            }
        };
        loadData();
    }, []);

    // Auto-detect CPF/CNPJ based on length
    useEffect(() => {
        if (selectedFornecedor) {
            const doc = selectedFornecedor.cnpj_cpf.replace(/\D/g, '');
            if (doc.length > 11) {
                setTipoDocumento('CNPJ');
            } else {
                setTipoDocumento('CPF');
            }
        }
    }, [selectedFornecedor]);

    const handleAddItem = () => {
        setItens([...itens, { id: Date.now().toString(), descricao: '', quantidade: '1', valor: '' }]);
    };

    const handleRemoveItem = (id: string) => {
        if (itens.length > 1) {
            setItens(itens.filter(i => i.id !== id));
        }
    };

    const handleItemChange = (id: string, field: keyof ItemMaterialForm, value: string) => {
        setItens(itens.map(i => i.id === id ? { ...i, [field]: value } : i));
    };

    const formatCurrencyDisplay = (value: number) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    };

    const areItensValid = itens.every(i => i.descricao.trim() !== '' && i.quantidade !== '' && i.valor !== '');
    const isFormValid = selectedFornecedor && selectedInvestidor && selectedObra && totalValor > 0 && areItensValid;

    return (
        <Container maxWidth="md">
            <Box className="no-print" sx={{ my: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom color="primary.main" sx={{ fontWeight: 'bold' }}>
                    Gerador de Recibos - Material
                </Typography>

                <Paper elevation={3} sx={{ p: 4 }}>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Autocomplete
                                options={investidores}
                                getOptionLabel={(option) => option.nome}
                                value={selectedInvestidor}
                                onChange={(_, newValue) => setSelectedInvestidor(newValue)}
                                renderInput={(params) => <TextField {...params} label="Comprador (Investidor)" required />}
                                renderOption={(props, option) => {
                                    const { key, ...otherProps } = props;
                                    return (
                                        <li key={option.id} {...otherProps}>
                                            {option.nome}
                                        </li>
                                    );
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Autocomplete
                                options={fornecedoresMaterial}
                                getOptionLabel={(option) => option.nome_razao_social}
                                value={selectedFornecedor}
                                onChange={(_, newValue) => setSelectedFornecedor(newValue)}
                                renderInput={(params) => <TextField {...params} label="Fornecedor (Beneficiário)" required />}
                                renderOption={(props, option) => {
                                    const { key, ...otherProps } = props;
                                    return (
                                        <li key={option.id} {...otherProps}>
                                            {option.nome_razao_social}
                                        </li>
                                    );
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl component="fieldset">
                                <FormLabel component="legend">Documento do Beneficiário</FormLabel>
                                <RadioGroup
                                    row
                                    value={tipoDocumento}
                                    onChange={(e) => setTipoDocumento(e.target.value as 'CPF' | 'CNPJ')}
                                >
                                    <FormControlLabel value="CPF" control={<Radio />} label="CPF" />
                                    <FormControlLabel value="CNPJ" control={<Radio />} label="CNPJ" />
                                </RadioGroup>
                            </FormControl>
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <FormControl component="fieldset">
                                <FormLabel component="legend">Pagador Interveniente?</FormLabel>
                                <RadioGroup
                                    row
                                    value={pagadorInterveniente ? 'Sim' : 'Não'}
                                    onChange={(e) => {
                                        const isSim = e.target.value === 'Sim';
                                        setPagadorInterveniente(isSim);
                                        if (!isSim) setSelectedPagador(null);
                                    }}
                                >
                                    <FormControlLabel value="Não" control={<Radio />} label="Não (Padrão)" />
                                    <FormControlLabel value="Sim" control={<Radio />} label="Sim" />
                                </RadioGroup>
                            </FormControl>
                        </Grid>

                        {pagadorInterveniente && (
                            <Grid size={{ xs: 12 }}>
                                <Autocomplete
                                    options={pagadoresPossiveis}
                                    getOptionLabel={(option) => `${option.nome} (${option.tipo})`}
                                    value={selectedPagador}
                                    onChange={(_, newValue) => setSelectedPagador(newValue)}
                                    renderInput={(params) => <TextField {...params} label="Nome do Pagador" required />}
                                    renderOption={(props, option) => {
                                        const { key, ...otherProps } = props;
                                        return (
                                            <li key={`${option.tipo}-${option.id}`} {...otherProps}>
                                                {option.nome} ({option.tipo})
                                            </li>
                                        );
                                    }}
                                />
                            </Grid>
                        )}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Autocomplete
                                options={obras}
                                getOptionLabel={(option) => option.nome}
                                value={selectedObra}
                                onChange={(_, newValue) => setSelectedObra(newValue)}
                                renderInput={(params) => <TextField {...params} label="Referente a (Obra)" required />}
                                renderOption={(props, option) => {
                                    const { key, ...otherProps } = props;
                                    return (
                                        <li key={option.id} {...otherProps}>
                                            {option.nome}
                                        </li>
                                    );
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Box sx={{ mt: 2, mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="h6">Itens do Recibo</Typography>
                                <Button startIcon={<AddIcon />} variant="outlined" onClick={handleAddItem} size="small">
                                    Adicionar Item
                                </Button>
                            </Box>
                            
                            {itens.map((item, index) => (
                                <Grid container spacing={2} key={item.id} sx={{ mb: 2, alignItems: 'center' }}>
                                    <Grid size={{ xs: 12, md: 5 }}>
                                        <TextField
                                            label="Descrição do Material"
                                            value={item.descricao}
                                            onChange={(e) => handleItemChange(item.id, 'descricao', e.target.value)}
                                            fullWidth
                                            required
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 4, md: 2 }}>
                                        <TextField
                                            label="Quantidade"
                                            value={item.quantidade}
                                            onChange={(e) => handleItemChange(item.id, 'quantidade', e.target.value)}
                                            fullWidth
                                            required
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 6, md: 4 }}>
                                        <TextField
                                            label="Valor (R$)"
                                            value={item.valor}
                                            onChange={(e) => handleItemChange(item.id, 'valor', e.target.value)}
                                            fullWidth
                                            required
                                            type="number"
                                            size="small"
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 2, md: 1 }}>
                                        <IconButton 
                                            color="error" 
                                            onClick={() => handleRemoveItem(item.id)}
                                            disabled={itens.length === 1}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            ))}
                            
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, mb: 3 }}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Total: {formatCurrencyDisplay(totalValor)}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                label="Data"
                                type="date"
                                value={data}
                                onChange={(e) => setData(e.target.value)}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ max: "9999-12-31" }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                    label="Cidade"
                                    value={cidade}
                                    onChange={(e) => setCidade(e.target.value)}
                                    fullWidth
                                />
                                <TextField
                                    label="Estado"
                                    value={estado}
                                    onChange={(e) => setEstado(e.target.value)}
                                    sx={{ width: '80px' }}
                                />
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <FormControl component="fieldset">
                                <FormLabel component="legend">O Pagamento quita integralmente o valor dos materiais?</FormLabel>
                                <RadioGroup
                                    row
                                    value={quitaIntegralmente ? 'Sim' : 'Não'}
                                    onChange={(e) => setQuitaIntegralmente(e.target.value === 'Sim')}
                                >
                                    <FormControlLabel value="Sim" control={<Radio />} label="Sim" />
                                    <FormControlLabel value="Não" control={<Radio />} label="Não" />
                                </RadioGroup>
                            </FormControl>
                        </Grid>

                        <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<PrintIcon />}
                                onClick={handlePrint}
                                disabled={!isFormValid}
                            >
                                Imprimir Recibo
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>

            </Box>

            {/* Hidden Print Component */}
            <div className="print-only">
                {selectedFornecedor && selectedInvestidor && (
                    <ReciboPrint
                        ref={componentRef}
                        fornecedor={selectedFornecedor}
                        investidor={selectedInvestidor}
                        unidade={null}
                        obra={selectedObra}
                        valor={totalValor}
                        data={new Date(data + 'T12:00:00')}
                        descricao="materiais de construção"
                        cidade={cidade}
                        estado={estado}
                        tipoDocumentoBeneficiario={tipoDocumento}
                        pagador={selectedPagador}
                        quitaIntegralmente={quitaIntegralmente}
                        tipoRecibo="material"
                        itens={itens.map(i => ({ ...i, valor: parseFloat(i.valor) || 0 })) as ReciboItem[]}
                    />
                )}
            </div>
        </Container >
    );
};

export default RecibosMaterialPage;
